<script lang="ts">
    import { get } from 'svelte/store';
    import { _ } from 'svelte-i18n';
    import { api } from '../../../stores/api';
    import ButtonMain from '../../shared/ButtonMain.svelte';
    import WarningColor from '../../shared/WarningColor.svelte';
    import SuccessColor from '../../shared/SuccessColor.svelte';
    import ErrorColor from '../../shared/ErrorColor.svelte';
    import Label from '../../shared/Label.svelte';
    import { CustomerAccountApi, type PreviewSummaryType, type ImportResultType } from './CustomerAccountApi';

    export let customerId: number;

    let exportLoading = false;
    let exportError = '';

    let fileInput: HTMLInputElement;
    let importFileContent: object | null = null;
    let importFileName = '';
    let importPreview: PreviewSummaryType | null = null;
    let importPreviewLoading = false;
    let importPreviewError = '';
    let importLoading = false;
    let importResult: ImportResultType | null = null;
    let importError = '';
    let overrideWebusers = false;

    async function handleExport() {
        exportLoading = true;
        exportError = '';
        try {
            const blob = await CustomerAccountApi.exportAccount(get(api), customerId);
            const date = new Date().toISOString().slice(0, 10);
            const filename = `account_customer_${customerId}_${date}.json`;
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (err: any) {
            console.error('Export error', err);
            exportError = $_('export_error', { values: { err: err?.message ?? String(err) } });
        } finally {
            exportLoading = false;
        }
    }

    async function handleFileChange(event: Event) {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (!file) return;

        importFileName = file.name;
        importPreview = null;
        importPreviewError = '';
        importResult = null;
        importError = '';
        importFileContent = null;

        try {
            const text = await file.text();
            const parsed = JSON.parse(text);
            importFileContent = parsed;

            importPreviewLoading = true;
            importPreview = await CustomerAccountApi.previewImport(get(api), parsed);
        } catch (err: any) {
            console.error('Preview error', err);
            importPreviewError = $_('read_file_error', { values: { err: err?.message ?? String(err) } });
            importFileContent = null;
        } finally {
            importPreviewLoading = false;
        }
    }

    async function handleImport() {
        if (!importFileContent) return;
        if (!confirm($_('confirm_import_account'))) return;

        importLoading = true;
        importResult = null;
        importError = '';
        try {
            importResult = await CustomerAccountApi.importAccount(get(api), importFileContent, overrideWebusers);
        } catch (err: any) {
            console.error('Import error', err);
            importError = $_('import_error', { values: { err: err?.message ?? String(err) } });
        } finally {
            importLoading = false;
        }
    }
</script>

<div class="export-import-container">
    <div class="section">
        <div class="section-title">
            <Label>{$_('export_account_to_json')}</Label>
        </div>
        <div class="section-content">
            <ButtonMain
                text={exportLoading ? $_('exporting') : $_('export_account_btn')}
                disabled={exportLoading}
                on:click={handleExport}
            />
            {#if exportError}
                <div style="margin-top: 8px;">
                    <ErrorColor>{exportError}</ErrorColor>
                </div>
            {/if}
        </div>
    </div>

    <div class="section" style="margin-top: 20px;">
        <div class="section-title">
            <Label>{$_('import_account_from_json')}</Label>
        </div>
        <div class="section-content">
            <!-- svelte-ignore a11y-click-events-have-key-events -->
            <!-- svelte-ignore a11y-no-static-element-interactions -->
            <input
                type="file"
                accept=".json"
                style="display: none;"
                bind:this={fileInput}
                on:change={handleFileChange}
            />
            <ButtonMain
                text={$_('select_json_file')}
                disabled={importLoading}
                on:click={() => fileInput?.click()}
            />

            {#if importFileName}
                <div style="margin-top: 8px; font-size: 12px; color: #555;">
                    {$_('file_label')} <strong>{importFileName}</strong>
                </div>
            {/if}

            {#if importPreviewLoading}
                <div style="margin-top: 8px; font-size: 12px;">{$_('loading_preview')}</div>
            {/if}

            {#if importPreviewError}
                <div style="margin-top: 8px;">
                    <ErrorColor>{importPreviewError}</ErrorColor>
                </div>
            {/if}

            {#if importPreview}
                <div class="preview-box">
                    <div class="preview-row">
                        <span class="preview-label">{$_('account_label')}</span>
                        <span>{importPreview.customer_name}</span>
                    </div>
                    <div class="preview-row">
                        <span class="preview-label">{$_('export_date_label')}</span>
                        <span>{importPreview.export_date}</span>
                    </div>
                    <div class="preview-row">
                        <span class="preview-label">{$_('version_label')}</span>
                        <span>{importPreview.export_version}</span>
                    </div>
                    <div class="preview-counts">
                        {#each Object.entries(importPreview.counts) as [key, count]}
                            <span class="count-badge">{key}: {count}</span>
                        {/each}
                    </div>
                    <div style="margin-top: 8px;">
                        <WarningColor>{$_('import_new_customer_id_warning')}</WarningColor>
                    </div>
                </div>

                <div style="margin-top: 10px;">
                    <label class="override-label">
                        <input type="checkbox" bind:checked={overrideWebusers} />
                        {$_('override_webusers')}
                    </label>
                </div>

                <div style="margin-top: 8px;">
                    <ButtonMain
                        text={importLoading ? $_('importing') : $_('import_btn')}
                        disabled={importLoading}
                        on:click={handleImport}
                    />
                </div>
            {/if}

            {#if importError}
                <div style="margin-top: 8px;">
                    <ErrorColor>{importError}</ErrorColor>
                </div>
            {/if}

            {#if importResult}
                <div style="margin-top: 8px;">
                    <SuccessColor>
                        {$_('import_success_new_customer_id')} <strong>{importResult.new_customer_id}</strong>
                    </SuccessColor>
                    <div class="preview-counts" style="margin-top: 6px;">
                        {#each Object.entries(importResult.summary) as [key, count]}
                            <span class="count-badge">{key}: {count}</span>
                        {/each}
                    </div>
                </div>
            {/if}
        </div>
    </div>
</div>

<style>
    .export-import-container {
        padding: 0;
    }
    .section-title {
        font-size: 14px;
        padding: 2px 5px;
        margin-bottom: 8px;
    }
    .section-content {
        padding: 0 5px;
    }
    .preview-box {
        margin-top: 12px;
        padding: 10px 12px;
        background: var(--selected-item-background, #f5f5f5);
        border-left: 3px solid #ccc;
        font-size: 13px;
    }
    .preview-row {
        display: flex;
        gap: 8px;
        margin-bottom: 4px;
    }
    .preview-label {
        color: #666;
        min-width: 110px;
    }
    .preview-counts {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        margin-top: 8px;
    }
    .count-badge {
        background: #e0e0e0;
        border-radius: 3px;
        padding: 2px 7px;
        font-size: 12px;
    }
    .override-label {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 13px;
        cursor: pointer;
        user-select: none;
    }
</style>
